/**
 * Security utility for password obfuscation
 * Note: This provides obfuscation, not true security.
 * Real security should be implemented server-side.
 */

// Simple XOR cipher with rotating key
const encodePassword = (text, key = "BestCall2024") => {
  let result = "";
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i);
    const keyCode = key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode ^ keyCode);
  }
  return btoa(result); // Base64 encode the result
};

const decodePassword = (encoded, key = "BestCall2024") => {
  try {
    const decoded = atob(encoded); // Base64 decode
    let result = "";
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i);
      const keyCode = key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode ^ keyCode);
    }
    return result;
  } catch (error) {
    return null;
  }
};

// Additional obfuscation - split and reassemble
const createPasswordParts = () => {
  // Split the password into parts and scramble them
  const parts = ["#BC_", "vOiC", "e_20"];
  const scrambled = [0, 1, 2]; // Index order to reassemble
  return { parts, scrambled };
};

const assemblePassword = (parts, scrambled) => {
  let password = "";
  scrambled.forEach((index) => {
    password += parts[index];
  });
  return password;
};

// Main password validation with multiple obfuscation layers
class SecureAuth {
  // Store encoded password (this will look like gibberish in the code)
  static #encodedPassword = "YScwKzUuBS9XbwAE"; // This is "#BC_vOiCe_20" encoded

  // Alternative method using character codes and math
  static #getPasswordFromCodes() {
    // ASCII codes for "#BC_vOiCe_20" with simple math obfuscation
    const codes = [65, 97, 99, 128, 152, 114, 141, 104, 139, 134, 80, 79];
    const key = [30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 30, 31];

    return codes.map((code, i) => String.fromCharCode(code - key[i])).join("");
  }

  // Third method using parts assembly
  static #getPasswordFromParts() {
    const { parts, scrambled } = createPasswordParts();
    return assemblePassword(parts, scrambled);
  }

  // Validate password using multiple methods as fallback
  static validatePassword(inputPassword) {
    try {
      // Method 1: XOR encoded
      const decoded1 = decodePassword(this.#encodedPassword);
      if (decoded1 && inputPassword === decoded1) {
        return true;
      }

      // Method 2: Character codes
      const password2 = this.#getPasswordFromCodes();
      if (inputPassword === password2) {
        return true;
      }

      // Method 3: Parts assembly
      const password3 = this.#getPasswordFromParts();
      if (inputPassword === password3) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // Additional security: Rate limiting
  static #attemptLog = [];
  static #maxAttempts = 5;
  static #lockoutDuration = 5 * 60 * 1000; // 5 minutes

  static isLocked() {
    const now = Date.now();
    // Clean old attempts
    this.#attemptLog = this.#attemptLog.filter(
      (time) => now - time < this.#lockoutDuration
    );
    return this.#attemptLog.length >= this.#maxAttempts;
  }

  static recordAttempt() {
    this.#attemptLog.push(Date.now());
  }

  static getRemainingLockTime() {
    if (!this.isLocked()) return 0;
    const oldestAttempt = Math.min(...this.#attemptLog);
    const unlockTime = oldestAttempt + this.#lockoutDuration;
    return Math.max(0, unlockTime - Date.now());
  }

  // Main login method with rate limiting
  static login(password) {
    if (this.isLocked()) {
      const remainingTime = Math.ceil(this.getRemainingLockTime() / 1000);
      throw new Error(
        `Prea multe incercari esuate. Incercati din nou in ${remainingTime} secunde.`
      );
    }

    const isValid = this.validatePassword(password);

    if (!isValid) {
      this.recordAttempt();
    } else {
      // Clear attempts on successful login
      this.#attemptLog = [];
    }

    return isValid;
  }
}

export default SecureAuth;
